import cloneDeep from 'lodash-es/cloneDeep';

export class CloneUtils {
    private constructor() {}

    /**
     * Creates a deep clone of the provided value (using lodash).
     * It does not preserve class prototypes.
     */
    public static cloneData<T>(source: T): T {
        return cloneDeep(source);
    }

    /**
     * Creates a deep clone of an object and restores the prototype of the outer object so that the cloned
     * value keeps the same class/type at the top level.
     *
     * Notes:
     * - Uses lodash for the deep clone itself.
     * - Only the prototype of the outer object is restored.
     * - Nested class instances are NOT reconstructed and remain in the shape produced by lodash.
     * - Throws if the provided value is not an object.
     */
    public static cloneClass<T extends object>(source: T): T {
        if (source === null || typeof source !== 'object') {
            throw new Error('The provided parameter is not an object.');
        }
        return this._cloneClass(source);
    }

    /**
     * Like cloneClass, but tolerant for non-object values.
     * While cloneClass throws an error for non-object values, cloneClassSafe returns the value as is.
     *
     * Behavior:
     * - If the source is an object, it behaves like cloneClass.
     * - If the source is null, undefined, or a primitive type, its value is returned.
     */
    public static cloneClassSafe<T>(source: T): T {
        if (source === null || typeof source !== 'object') {
            return source;
        }
        return this._cloneClass(source);
    }

    private static _cloneClass<T extends object>(source: T): T {
        const clone = this.cloneData(source);
        Object.setPrototypeOf(clone, Object.getPrototypeOf(source));
        return clone;
    }
}
